import axios from 'axios';

import { toQueryParams, getAuthTokenFromStorage, getUserIdFromStorage } from '../utils';
import { getServerUrl } from '../constants';

const SUBROUTE = 'election';

/**
 * @description gets all elections from address
 * @param {String} address address
 * @returns {Promise<Object>} API response
 */
export const getAllFromAddress = async (address) => {
  const queryParams = toQueryParams({
    address,
  });

  const url = `${global.API_URL}/${SUBROUTE}/address?${queryParams}`;

  try {
    const { data: { data } } = await axios.get(url);
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * @description search for all elections by title
 * @param {String} title title of election to search on
 * @returns {Promise<Object>} API response
 */
export const searchElections = async (title) => {
  const queryParams = toQueryParams({
    title,
  });

  const url = `${global.API_URL}/${SUBROUTE}/search?${queryParams}`;

  try {
    const { data: { data } } = await axios.get(url);
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * @description gets all elections
 * @returns {Promise<Object>} API response
 */
export const fetchAll = async () => {
  if (!global.API_URL) {
    global.API_URL = getServerUrl();
  }

  const url = `${global.API_URL}/${SUBROUTE}`;

  try {
    const { data: { data } } = await axios.get(url);
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * @description gets all elections in the future
 * @returns {Promise<Object>} API response
 */
export const fetchAllFutureElections = async () => {
  if (!global.API_URL) {
    global.API_URL = getServerUrl();
  }

  const url = `${global.API_URL}/${SUBROUTE}/future`;

  try {
    const { data: { data } } = await axios.get(url);
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * @description gets all elections in the future that are approved
 * @returns {Promise<Object>} API response
 */
export const fetchAllApprovedFutureElections = async () => {
  if (!global.API_URL) {
    global.API_URL = getServerUrl();
  }

  const url = `${global.API_URL}/${SUBROUTE}/future/approved`;

  try {
    const { data: { data } } = await axios.get(url);
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * @description gets all elections in the future that are unapproved
 * @returns {Promise<Object>} API response
 */
export const fetchAllUnapprovedFutureElections = async () => {
  if (!global.API_URL) {
    global.API_URL = getServerUrl();
  }

  const url = `${global.API_URL}/${SUBROUTE}/future/unapproved`;

  try {
    const { data: { data } } = await axios.get(url);
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * @description creates an election object in the database
 * @param {Object} fields object with data to send
 * @param {String} fields.title title of election
 * @param {String} fields.state state code (e.g. VT) where election takes place
 * @param {Date|String} fields.date date in string format of election
 * @param {Array<String>} fields.races array of race ids in the election
 * @param {Array<Object>} polling_locations array of polling location objects (address, name, hours.start_time, hours.end_time)
 * @returns {Object} api response
 */
export const createElection = async (fields) => {
  const url = `${global.API_URL}/${SUBROUTE}`;
  const token = getAuthTokenFromStorage();

  try {
    const { data: { data } } = await axios.post(url, {
      ...fields,
      created_by: getUserIdFromStorage(),
    }, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    });
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * @description adds raceId to electionId
 * @param {String} electionId id of election
 * @param {String} raceId id of race
 * @returns {Promise<Object>} API response
 */
export const addRaceToElection = async (electionId, raceId) => {
  if (!global.API_URL) {
    global.API_URL = getServerUrl();
  }

  const url = `${global.API_URL}/${SUBROUTE}/add-race/${electionId}`;
  const token = getAuthTokenFromStorage();

  try {
    const { data: { data } } = await axios.put(url, { raceId }, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    });
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * @description approves an election object
 * @param {String} id id of election object
 * @returns {Promise<Object>} API response
 */
export const adminApproveElection = async (id) => {
  if (!global.API_URL) {
    global.API_URL = getServerUrl();
  }

  const url = `${global.API_URL}/${SUBROUTE}/admin/approve/${id}`;
  const token = getAuthTokenFromStorage();

  try {
    const { data: { data } } = await axios.get(url, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    });
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

/**
 * @description unapproves an election object
 * @param {String} id id of election object
 * @returns {Promise<Object>} API response
 */
export const adminUnapproveElection = async (id) => {
  if (!global.API_URL) {
    global.API_URL = getServerUrl();
  }

  const url = `${global.API_URL}/${SUBROUTE}/admin/unapprove/${id}`;
  const token = getAuthTokenFromStorage();

  try {
    const { data: { data } } = await axios.get(url, {
      headers: {
        authorization: `Bearer ${token}`,
      },
    });
    return data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};
